import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { categoryOf } from '@/constants/categories';
import { FallbackPoster, DISCOVER_CARD_BADGES, POSTER_PAPER_HEX, POSTER_PAPER_DIM, POSTER_PAPER_SOFT, POSTER_CHAMPAGNE_HEX } from '@/components/ui/FallbackPoster';
import { EditionBadge } from '@/components/ui/EditionBadge';
import { CatIcon } from '@/components/ui/CatIcon';
import { formatDayLong, formatDayShort, formatTime, formatDayNum, formatMonthShort, hasKnownTime } from '@/lib/dates';
import { upperCity } from '@/lib/city';
import { getResponsiveImageProps } from '@/lib/images';
import type { EventRow } from '@/types/database';

interface Props { event: EventRow & { distance_km?: number | null }; variant?: 'hero' | 'magazine' | 'row' | 'peek' | 'grid'; onTap?: () => void }

function Tappable({ to, onTap, className, children }: { to: string; onTap?: () => void; className?: string; children: ReactNode }) {
  // Toujours un vrai lien (href crawlable pour le SEO, ouverture possible dans
  // un nouvel onglet) ; onTap conserve l'ouverture en sheet au clic simple.
  if (onTap) {
    return (
      <Link
        to={to}
        onClick={(ev) => { ev.preventDefault(); onTap(); }}
        className={`block text-left ${className ?? ''}`}
      >
        {children}
      </Link>
    );
  }
  return <Link to={to} className={className}>{children}</Link>;
}

export function EventCard({ event: e, variant = 'magazine', onTap }: Props) {
  const meta = categoryOf(e.kind);
  const to = `/event/${e.id}`;
  const hasImage = Boolean(e.image_url);
  const heroImage = getResponsiveImageProps(e.image_url, {
    widths: [320, 480, 640, 960],
    sizes: '(max-width: 640px) 86vw, 520px',
    fallbackWidth: 480,
  });
  const peekImage = getResponsiveImageProps(e.image_url, {
    widths: [320, 480, 640],
    sizes: '300px',
    fallbackWidth: 320,
  });
  const cardImage = getResponsiveImageProps(e.image_url, {
    widths: [320, 480, 640, 960],
    sizes: '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw',
    fallbackWidth: 480,
  });

  if (variant === 'hero') {
    return (
      <Tappable to={to} onTap={onTap} className="block group">
        <div className="relative mx-auto" style={{ maxWidth: 520 }}>
          {hasImage
            ? <img src={heroImage?.src ?? e.image_url ?? ''} srcSet={heroImage?.srcSet} sizes={heroImage?.sizes} alt="" className="w-full aspect-square object-cover rounded-md" loading="lazy" decoding="async" />
            : <FallbackPoster kind={e.kind} title={e.title} city={e.city} startsAt={e.starts_at} ratio="portrait" minimal className="!aspect-square" />}
          <div className="absolute top-3 left-3 right-3 flex justify-between items-start pointer-events-none">
            <span className="t-cap text-paper-elev/90 drop-shadow">À LA UNE</span>
            <EditionBadge seed={e.id} className="text-paper-elev/90 drop-shadow" />
          </div>
        </div>
        <div className="pt-4 mx-auto" style={{ maxWidth: 520 }}>
          <div className="t-cap mb-1.5">{meta.label}{e.city ? <> · <span className="text-ink-3">{upperCity(e.city)}</span></> : null}</div>
          <h3 className="t-ed-2xl mb-2">{e.title}</h3>
          <div className="flex items-center gap-3 text-ink-2">
            <span className="t-mono-sm">{formatDayLong(e.starts_at).toUpperCase()}{hasKnownTime(e.starts_at) && ` · ${formatTime(e.starts_at)}`}</span>
          </div>
        </div>
      </Tappable>
    );
  }

  if (variant === 'row') {
    return (
      <Tappable to={to} onTap={onTap} className="flex gap-4 items-start py-3 group">
        <div className="flex flex-col items-center justify-center w-14 shrink-0">
          <span className="t-mono-xs text-ink-3">{formatMonthShort(e.starts_at).toUpperCase()}</span>
          <span className="font-serif text-[28px] leading-none">{formatDayNum(e.starts_at)}</span>
          {hasKnownTime(e.starts_at) && <span className="t-mono-xs text-ink-3 mt-0.5">{formatTime(e.starts_at)}</span>}
        </div>
        <div className="flex-1 min-w-0">
          <div className="t-cap mb-1 inline-flex items-center gap-1.5">
            <CatIcon kind={meta.iconKey} size={12} /> {meta.label}
          </div>
          <h3 className="t-ed-md leading-tight mb-1 line-clamp-2">{e.title}</h3>
          <div className="t-ui-sm text-ink-3 truncate">
            {upperCity(e.city) ?? '—'}{e.distance_km != null && <> · {e.distance_km.toFixed(1)} km</>}
          </div>
        </div>
      </Tappable>
    );
  }

  if (variant === 'grid') {
    return (
      <Tappable to={to} onTap={onTap} className="group row-span-2 grid grid-rows-subgrid gap-y-0 content-start">
        <div className="relative w-full aspect-[4/3] rounded-md overflow-hidden">
          {hasImage
            ? <img src={cardImage?.src ?? e.image_url ?? ''} srcSet={cardImage?.srcSet} sizes={cardImage?.sizes} alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" decoding="async" />
            : <FallbackPoster kind={e.kind} title={e.title} city={e.city} startsAt={e.starts_at} ratio="landscape" minimal className="absolute inset-0 !aspect-auto !rounded-none" />}
        </div>
        <div className="pt-2.5 pb-8">
          <div className="t-cap mb-1">{meta.label}</div>
          <h3 className="t-ed-md leading-tight line-clamp-2 mb-1">{e.title}</h3>
          <span className="t-mono-xs text-ink-3">{formatDayShort(e.starts_at).toUpperCase()}{hasKnownTime(e.starts_at) && ` · ${formatTime(e.starts_at)}`}</span>
        </div>
      </Tappable>
    );
  }

  if (variant === 'peek') {
    return (
      <Tappable to={to} onTap={onTap} className="block w-[160px] shrink-0">
        <div className="relative w-full aspect-[4/3] rounded-md overflow-hidden bg-paper-elev">
          {hasImage
            ? <img src={peekImage?.src ?? e.image_url ?? ''} srcSet={peekImage?.srcSet} sizes={peekImage?.sizes} alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" decoding="async" />
            : <div className="absolute inset-0" style={{ backgroundColor: `rgb(var(${meta.colorVar}))` }} />}
        </div>
        <div className="pt-2">
          <div className="t-cap mb-0.5">{meta.label}</div>
          <h3 className="t-ed-sm leading-tight line-clamp-2 mb-0.5">{e.title}</h3>
          <span className="t-mono-xs text-ink-3">{formatDayShort(e.starts_at).toUpperCase()}</span>
        </div>
      </Tappable>
    );
  }

  // Default = magazine
  return (
    <Tappable to={to} onTap={onTap} className="block group">
      <div className="relative w-full aspect-[16/9] rounded-md overflow-hidden">
        {hasImage ? (
          <>
            <img
              src={cardImage?.src ?? e.image_url ?? ''}
              srcSet={cardImage?.srcSet}
              sizes={cardImage?.sizes}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
              decoding="async"
            />
            <div
              className="absolute inset-0"
              style={{ background: 'linear-gradient(to top, rgb(0,0,0) 0%, rgba(0,0,0,0.32) 70%, rgba(0,0,0,0.18) 100%)' }}
            />
            <div className="absolute inset-0 flex flex-col p-4">
              <div className="flex items-start justify-between gap-3">
                <span className="t-mono-sm max-w-[42%] truncate uppercase tracking-[0.08em]" style={{ color: POSTER_PAPER_DIM }}>
                  {DISCOVER_CARD_BADGES[e.kind]}
                </span>
                <span className="t-mono-sm min-w-0 max-w-[58%] text-right leading-[1.2] tracking-[0.04em]" style={{ color: POSTER_PAPER_DIM }}>
                  {formatDayShort(e.starts_at).toUpperCase()}{hasKnownTime(e.starts_at) && ` · ${formatTime(e.starts_at)}`}
                </span>
              </div>
              <div className="flex flex-1 items-end pb-1">
                <h3
                  className="max-w-[84%] font-serif text-[clamp(20px,5.4vw,24px)] italic leading-[0.92] tracking-[-0.02em] line-clamp-3"
                  style={{ color: POSTER_PAPER_HEX }}
                >
                  {e.title}
                </h3>
              </div>
              <div className="pr-9">
                <span className="mb-3 block h-px w-10" style={{ backgroundColor: POSTER_CHAMPAGNE_HEX }} />
                <div className="flex justify-between items-center">
                  <div className="font-mono text-[11px] font-medium uppercase leading-[14px] tracking-[0.08em]" style={{ color: POSTER_PAPER_SOFT }}>
                    {upperCity(e.city) ?? '—'}
                  </div>
                  {e.distance_km != null && (
                    <span className="t-mono-xs" style={{ color: POSTER_PAPER_DIM }}>{e.distance_km.toFixed(1)} km</span>
                  )}
                </div>
              </div>
            </div>
          </>
        ) : (
          <FallbackPoster kind={e.kind} title={e.title} city={e.city} startsAt={e.starts_at} ratio="landscape" layout="discover-card" seed={e.id} />
        )}
      </div>
    </Tappable>
  );
}
